import { parseOgcServiceUrl } from './parseOgcServiceUrl';
import type {
  RasterCompanionConfig,
  RasterCompanionProtocol,
  ResolvedRasterCompanion,
} from './rasterCompanionTypes';

function normalizeLayerNames(config: RasterCompanionConfig): string[] {
  return [
    ...new Set(
      (config.layer_names || [])
        .map(name => name?.trim())
        .filter((name): name is string => Boolean(name)),
    ),
  ];
}

function normalizeProtocol(
  protocol?: RasterCompanionProtocol,
): RasterCompanionProtocol {
  return protocol === 'wmts' ? 'wmts' : 'wms';
}

/**
 * Resolve a raster companion from Andromeda `attributes.raster_companion`.
 * Returns null when disabled / incomplete — WFS loads alone.
 */
export function resolveRasterCompanion(args: {
  rasterCompanion?: RasterCompanionConfig | null;
  /** Auth (and other) params from the WFS URL; merged with raster URL params. */
  wfsCustomParameters?: Record<string, string>;
}): ResolvedRasterCompanion | null {
  const config = args.rasterCompanion;
  if (!config?.enabled) {
    return null;
  }

  const rawUrl = config.service_url?.trim();
  const layerNames = normalizeLayerNames(config);
  if (!rawUrl || layerNames.length === 0) {
    return null;
  }

  const protocol = normalizeProtocol(config.protocol);
  const parsed = parseOgcServiceUrl(rawUrl);
  const customParameters = {
    ...args.wfsCustomParameters,
    ...parsed.customParameters,
  };

  return {
    protocol,
    label: layerNames[0],
    serviceUrl: parsed.url,
    layerNames,
    ...(protocol === 'wms' && config.custom_layer_parameters
      ? { customLayerParameters: config.custom_layer_parameters }
      : {}),
    ...(Object.keys(customParameters).length > 0 ? { customParameters } : {}),
  };
}
